/**
 * THE DISCLAIMER, LIVE IN DESIGN EMAIL (Jack, 2026-10-19: "just populates the disclaimer as
 * things happen so it's less confusing").
 *
 * Step 4 builds the disclaimer from the template's default plus what the email says. Until
 * now that text only replaced the footer in the form's preview and the submitted email, so
 * Design Email kept showing Layne's "use the disclosure builder" placeholder and an editor
 * could think their disclaimer was gone. These helpers let the Studio draw the same text into
 * its footer block as the email changes. FormView and the Studio read the email's copy the
 * same way (`disclaimerCopy`), so the two can never disagree about which lines apply.
 */
import { FULL_REGISTRY, blockLabel } from "../email/blocks";
import { echNameOf } from "./echRegistry";
import { AMEN_LIB_BLOCKS } from "./echAmen";
import { detect, assemble, emailText, applyDisclaimer, type Answers, type CopyPiece } from "./disclaimer";

type Doc = Array<{ type?: string; data?: Record<string, unknown> }>;

/** a footer block the builder writes into (ECH_Disclaimer, ECH_DisclaimerMortgage, …) */
export function isDisclaimerBlock(type: string): boolean {
  return /^ECH_Disclaimer/i.test(echNameOf(String(type || "")) || "");
}

/**
 * Every piece of copy the email carries, labelled with where it lives, plus the amenity
 * summary. `emailHtml` is the email as it reads (fill, duplicate, listing rows) for anything
 * no field held. Moved here unchanged from FormView's `dsCopy`.
 */
export function disclaimerCopy(src: Doc, form: { subject?: string; preheader?: string; headline?: string }, emailHtml: string): { pieces: CopyPiece[]; amen: string } {
  const out: CopyPiece[] = [
    { where: "Subject line", text: form.subject || "" },
    { where: "Preheader", text: form.preheader || "" },
    { where: "Headline", text: form.headline || "" },
  ];
  let amen = "";
  (src || []).forEach((b) => {
    const type = b.type || "";
    if (/disclaimer/i.test(echNameOf(type))) return;   /* the footer we write, not copy */
    const blk = blockLabel(type);
    const fields = (FULL_REGISTRY[type] && FULL_REGISTRY[type].fields) || [];
    const fl = (k: string) => { const f = fields.find((x) => x.k === k); return (f && f.label) || k; };
    Object.entries(b.data || {}).forEach(([k, v]) => {
      if (k.startsWith("__") || typeof v !== "string") return;
      const t = v.trim();
      if (t && !/^(https?:|data:|#|mailto:|tel:)/i.test(t)) out.push({ where: blk + " · " + fl(k), text: t.replace(/<[^>]+>/g, " ") });
    });
    /* an amenity-icon block with icons picked describes the community's amenities */
    if (!amen && (AMEN_LIB_BLOCKS.indexOf(echNameOf(type)) >= 0 || type === "amenities")) {
      const d = b.data || {};
      const labels = Object.keys(d).filter((k) => /^label\d+$/.test(k) && typeof d[k] === "string" && (d[k] as string).trim()
        && String(d["icon" + k.slice(5)] || "").trim()).map((k) => (d[k] as string).trim());
      const picks = Array.isArray(d.picks) ? (d.picks as unknown[]).map(String).filter(Boolean) : [];
      const names = labels.length ? labels : picks;
      if (names.length) amen = blk + " (" + names.slice(0, 3).join(", ") + (names.length > 3 ? "…" : "") + ")";
    }
  });
  /* last: the email as it reads, for anything no field held */
  out.push({ where: "the email", text: emailText(emailHtml || "") });
  return { pieces: out, amen };
}

export interface LiveDisclaimerInput {
  doc: Doc;
  template: string;
  reqFocus?: string;
  form: { subject?: string; preheader?: string; headline?: string; divisionCode?: string; targetDeploy?: string };
  answers?: Answers;
  requestNo?: string;
  emailHtml?: string;
  /** the Seller's Broker / Construction License line (disclaimerLicense.ts); see Ctx.license */
  license?: string | null;
}

/** the disclaimer paragraphs Step 4 would build for this email right now */
export function liveDisclaimerParas(i: LiveDisclaimerInput): string[] {
  const c = disclaimerCopy(i.doc, i.form, i.emailHtml || "");
  const det = detect({ template: i.template, reqFocus: i.reqFocus, copy: c.pieces, amenities: c.amen });
  return assemble(det, { ...(i.answers || {}), requestNo: i.requestNo || "" }, { division: i.form.divisionCode || "", deploy: i.form.targetDeploy, license: i.license });
}

/**
 * A footer block's markup with the disclaimer drawn in. An override (typed in the markup
 * Edit Email view) wins over the built text; blank lines separate paragraphs there.
 */
export function footerWithDisclaimer(html: string, paras: string[], override?: string): string {
  const o = String(override || "").trim();
  const use = o ? o.split(/\n\s*\n/).map((p) => p.trim()).filter(Boolean) : paras;
  return use.length ? applyDisclaimer(html, use) : html;
}
